(() => {
  const southernCountries = new Set(
    'AU NZ ZA AR CL UY PY BO PE BR NA BW ZW MZ MG MU RE LS SZ ZM MW TZ AO NC PF FJ WS TO PG SB VU TL ID'.split(' ')
  );

  const defaults = {
    spring: '03-01',
    summer: '06-01',
    autumn: '09-01',
    winter: '12-01'
  };

  const opposite = {
    spring: 'autumn',
    summer: 'winter',
    autumn: 'spring',
    winter: 'summer'
  };

  function resolveHemisphere(country, override = 'auto') {
    return override === 'north' || override === 'south'
      ? override
      : southernCountries.has(String(country).toUpperCase())
        ? 'south'
        : 'north';
  }

  function seasonForDate(
    date,
    starts = defaults,
    hemisphere = 'north',
    timeZone = 'Europe/Rome'
  ) {
    const parts = new Intl.DateTimeFormat('en-US', {
      timeZone,
      month: '2-digit',
      day: '2-digit'
    }).formatToParts(date);

    const today =
      Number(parts.find(p => p.type === 'month').value) * 100 +
      Number(parts.find(p => p.type === 'day').value);

    const schedule = Object.entries(defaults).map(([season, fallback]) => {
      const value = starts[season] || fallback;

      if (!/^\d{2}-\d{2}$/.test(value)) {
        throw new Error('Invalid season date');
      }

      const [month, day] = value.split('-').map(Number);
      const test = new Date(Date.UTC(2024, month - 1, day));

      if (
        month < 1 ||
        month > 12 ||
        day < 1 ||
        test.getUTCMonth() !== month - 1 ||
        test.getUTCDate() !== day
      ) {
        throw new Error('Invalid season date');
      }

      return { season, value: month * 100 + day };
    }).sort((a, b) => a.value - b.value);

    if (new Set(schedule.map(s => s.value)).size !== 4) {
      throw new Error('Duplicate season dates');
    }

    let selected = schedule[schedule.length - 1].season;

    for (const entry of schedule) {
      if (entry.value <= today) selected = entry.season;
    }

    return hemisphere === 'south' ? opposite[selected] : selected;
  }

  function selectSeasonVariant(variants, season) {
    return variants.find(
      variant => String(variant.season).toLowerCase() === season
    ) || null;
  }

  function buildCartItem({
    variant,
    recurring,
    selection,
    profile,
    season,
    year,
    hemisphere
  }) {
    if (!variant || !variant.available) {
      throw new Error('Unavailable box');
    }

    if (recurring && !variant.planId) {
      throw new Error('Missing selling plan');
    }

    if (!selection || !selection.variantId) {
      throw new Error('Missing selection');
    }

    const properties = {
      'First box selected item': selection.title,
      'First box selected variant': selection.variantTitle,
      'First box selected variant ID': String(selection.variantId),
      'First box selected SKU': selection.sku || '—',
      'First box selected category': selection.category,
      'First box season': `${season} ${year}`,
      'Season hemisphere': hemisphere,
      'Height (cm)': String(profile.heightCm),
      'Weight (kg)': String(profile.weightKg),
      'Usual top size': profile.topSize,
      'Usual bottom size': profile.bottomSize,
      'Preferred fit': profile.fit
    };

    if (profile.age) properties['Age group'] = profile.age;
    if (profile.notes) properties['Fit notes'] = profile.notes;

    const item = {
      id: String(variant.id),
      quantity: 1,
      properties
    };

    if (recurring) item.selling_plan = String(variant.planId);

    return item;
  }

  window.CZSeasonalBoxUtils = {
    resolveHemisphere,
    seasonForDate,
    selectSeasonVariant,
    buildCartItem
  };

  if (customElements.get('cz-seasonal-box')) return;

  class CZSeasonalBox extends HTMLElement {
    connectedCallback() {
      if (this.ready) return;
      this.ready = true;

      this.config = JSON.parse(
        this.querySelector('[data-cz-config]').textContent
      );

      this.labels = this.config.labels;
      this.mode = 'subscription';
      this.step = 0;
      this.selection = null;
      this.noneSelectedText = this.querySelector(
        '[data-cz-selection-summary]'
      ).textContent;

      this.busy = false;
      this.dialog = this.querySelector('dialog');
      this.form = this.querySelector('[data-cz-form]');
      this.hemisphere = resolveHemisphere(this.config.country);

      this.querySelector('[data-cz-hemisphere]').value = this.hemisphere;

      this.querySelector('[data-cz-hemisphere]').addEventListener(
        'change',
        event => {
          this.hemisphere = event.target.value;
          this.updateOffer();
        }
      );

      this.querySelectorAll('[data-cz-mode]').forEach(button => {
        button.addEventListener('click', () => {
          this.mode = button.dataset.czMode;
          this.updateOffer();
        });
      });

      this.querySelector('[data-cz-open]').addEventListener(
        'click',
        event => this.open(event.currentTarget)
      );

      this.querySelectorAll('[data-cz-close]').forEach(button => {
        button.addEventListener('click', () => this.dialog.close());
      });

      this.dialog.addEventListener('cancel', event => {
        if (this.busy) event.preventDefault();
      });

      this.dialog.addEventListener('close', () => {
        document.documentElement.classList.remove('cz-box-dialog-open');
        if (this.opener) this.opener.focus();
      });

      this.querySelectorAll('[data-cz-tab]').forEach(tab => {
        tab.addEventListener(
          'click',
          () => this.activateTab(tab.dataset.czTab)
        );
        tab.addEventListener('keydown', event => this.tabKeys(event));
      });

      this.querySelectorAll('[data-cz-product]').forEach(card => {
        const data = JSON.parse(
          card.querySelector('[data-cz-product-data]').textContent
        );

        card.querySelector('[data-cz-choice]').addEventListener(
          'change',
          () => this.choose(card, data)
        );

        card.querySelector('[data-cz-variant]').addEventListener(
          'change',
          () => {
            card.querySelector('[data-cz-choice]').checked = true;
            this.choose(card, data);
          }
        );
      });

      this.querySelector('[data-cz-units]').addEventListener(
        'change',
        () => this.updateUnits()
      );

      this.querySelector('[data-cz-next]').addEventListener(
        'click',
        () => this.next()
      );

      this.querySelector('[data-cz-back]').addEventListener(
        'click',
        () => this.showStep(this.step - 1)
      );

      this.form.addEventListener('submit', event => {
        event.preventDefault();
        if (this.step === 2) this.submit();
        else this.next();
      });

      this.activateTab('tops');
      this.updateUnits();
      this.updateOffer();
      this.querySelector('[data-cz-no-js]').hidden = true;
    }

    disconnectedCallback() {
      if (this.dialog && this.dialog.open) {
        this.dialog.close();
        document.documentElement.classList.remove('cz-box-dialog-open');
      }
    }

    text(selector, value) {
      this.querySelectorAll(selector).forEach(node => {
        node.textContent = value;
      });
    }

    updateOffer() {
      try {
        this.season = this.config.seasonOverride === 'auto'
          ? seasonForDate(
              new Date(),
              this.config.starts,
              this.hemisphere,
              this.config.timeZone
            )
          : this.config.seasonOverride;

        this.variant = selectSeasonVariant(
          this.config.variants,
          this.season
        );

        this.configError = false;
      } catch (_) {
        this.variant = null;
        this.configError = true;
      }

      const variant = this.variant;
      const recurring = this.mode === 'subscription';

      this.querySelectorAll('[data-cz-mode]').forEach(button => {
        button.setAttribute(
          'aria-pressed',
          String(button.dataset.czMode === this.mode)
        );
      });

      this.text('[data-cz-season]', this.labels[this.season] || '—');

      this.text(
        '[data-cz-sub-price]',
        variant && variant.planId ? variant.subscriptionPrice : '—'
      );

      this.text('[data-cz-once-price]', variant ? variant.price : '—');

      this.text(
        '[data-cz-current-price]',
        variant
          ? recurring
            ? variant.subscriptionPrice || '—'
            : variant.price
          : '—'
      );

      this.text(
        '[data-cz-current-mode]',
        recurring ? this.labels.subscription : this.labels.one_time
      );

      this.querySelectorAll('[data-cz-recurring-only]').forEach(node => {
        node.hidden = !recurring;
      });

      const open = this.querySelector('[data-cz-open]');

      const unavailable =
        !variant ||
        !variant.available ||
        recurring && !variant.planId;

      open.disabled = unavailable;

      this.text(
        '[data-cz-offer-error]',
        this.configError
          ? this.labels.config_error
          : !variant || !variant.available
            ? this.labels.box_unavailable
            : recurring && !variant.planId
              ? this.labels.plan_unavailable
              : ''
      );

      if (this.dialog.open && this.step === 2) this.renderReview();
    }

    open(opener) {
      if (
        !this.variant ||
        !this.variant.available ||
        this.mode === 'subscription' && !this.variant.planId
      ) return;

      this.opener = opener;
      this.dialog.showModal();
      document.documentElement.classList.add('cz-box-dialog-open');
      this.showStep(0);
    }

    activateTab(category, focus = false) {
      this.querySelectorAll('[data-cz-tab]').forEach(tab => {
        const selected = tab.dataset.czTab === category;
        tab.setAttribute('aria-selected', String(selected));
        tab.tabIndex = selected ? 0 : -1;
        if (selected && focus) tab.focus();
      });

      this.querySelectorAll('[data-cz-category]').forEach(panel => {
        panel.hidden = panel.dataset.czCategory !== category;
      });
    }

    tabKeys(event) {
      const tabs = Array.from(this.querySelectorAll('[data-cz-tab]'));
      let index = tabs.indexOf(event.currentTarget);

      if (event.key === 'ArrowRight') {
        index = (index + 1) % tabs.length;
      } else if (event.key === 'ArrowLeft') {
        index = (index + tabs.length - 1) % tabs.length;
      } else if (event.key === 'Home') {
        index = 0;
      } else if (event.key === 'End') {
        index = tabs.length - 1;
      } else {
        return;
      }

      event.preventDefault();
      this.activateTab(tabs[index].dataset.czTab, true);
    }

    choose(card, data) {
      const value = card.querySelector('[data-cz-variant]').value;
      const variant = data.variants.find(item => String(item.id) === value);
      if (!variant) return;

      this.selection = {
        title: data.title,
        handle: data.handle,
        category: data.category,
        variantId: variant.id,
        variantTitle: variant.title,
        sku: variant.sku
      };

      this.querySelectorAll('[data-cz-product]').forEach(item => {
        item.classList.toggle('is-selected', item === card);
      });

      this.text(
        '[data-cz-selection-summary]',
        `${this.labels.selected}: ${data.title} — ${variant.title}`
      );

      this.error('');
    }

    updateUnits() {
      const imperial = this.form.elements.units.value === 'imperial';

      this.querySelectorAll('[data-cz-metric]').forEach(node => {
        node.hidden = imperial;
        node.querySelectorAll('input').forEach(input => {
          input.disabled = imperial;
        });
      });

      this.querySelectorAll('[data-cz-imperial]').forEach(node => {
        node.hidden = !imperial;
        node.querySelectorAll('input').forEach(input => {
          input.disabled = !imperial;
        });
      });
    }

    validateProfile() {
      const panel = this.querySelector('[data-cz-step="1"]');

      for (const input of panel.querySelectorAll('input, select, textarea')) {
        if (!input.disabled && !input.reportValidity()) return false;
      }

      return true;
    }

    profile() {
      const fields = this.form.elements;
      const imperial = fields.units.value === 'imperial';

      return {
        heightCm: Math.round(
          Number(imperial ? fields.height_in.value : fields.height_cm.value) *
          (imperial ? 2.54 : 1) * 10
        ) / 10,
        weightKg: Math.round(
          Number(imperial ? fields.weight_lb.value : fields.weight_kg.value) *
          (imperial ? 0.45359237 : 1) * 10
        ) / 10,
        topSize: fields.top_size.value.trim(),
        bottomSize: fields.bottom_size.value.trim(),
        fit: fields.fit.value,
        age: fields.age.value,
        notes: fields.notes.value.trim()
      };
    }

    error(message) {
      const error = this.querySelector('[data-cz-error]');
      error.textContent = message;
      error.hidden = !message;
    }

    next() {
      if (this.step === 0 && !this.selection) {
        this.error(this.labels.choose_error);
        return;
      }

      if (this.step === 1 && !this.validateProfile()) return;

      this.showStep(Math.min(this.step + 1, 2));
    }

    showStep(step) {
      if (this.busy) return;

      this.step = Math.max(0, Math.min(step, 2));
      this.error('');

      this.querySelectorAll('[data-cz-step]').forEach(panel => {
        panel.hidden = Number(panel.dataset.czStep) !== this.step;
      });

      this.querySelectorAll('[data-cz-progress]').forEach(item => {
        if (Number(item.dataset.czProgress) === this.step) {
          item.setAttribute('aria-current', 'step');
        } else {
          item.removeAttribute('aria-current');
        }
      });

      this.querySelector('[data-cz-back]').hidden = this.step === 0;
      this.querySelector('[data-cz-next]').hidden = this.step === 2;
      this.querySelector('[data-cz-submit]').hidden = this.step !== 2;

      if (this.step === 2) this.renderReview();

      const heading = this.querySelector(
        `[data-cz-step="${this.step}"] [data-cz-step-heading]`
      );

      heading.focus();
      this.dialog.scrollTop = 0;
    }

    renderReview() {
      if (!this.selection) return;

      const profile = this.profile();

      this.text(
        '[data-cz-review-item]',
        `${this.selection.title} — ${this.selection.variantTitle}`
      );

      this.text(
        '[data-cz-review-profile]',
        `${profile.heightCm} cm / ${profile.weightKg} kg · ${this.labels.top_size}: ${profile.topSize} · ${this.labels.bottom_size}: ${profile.bottomSize} · ${this.labels[profile.fit] || profile.fit}`
      );

      this.querySelector('[data-cz-consent]').required =
        this.mode === 'subscription';

      this.querySelector('[data-cz-consent]').disabled =
        this.mode !== 'subscription';
    }

    async submit() {
      if (this.busy || !this.selection || !this.validateProfile()) return;

      const consent = this.querySelector('[data-cz-consent]');

      if (
        this.mode === 'subscription' &&
        !consent.reportValidity()
      ) return;

      this.updateOffer();

      if (
        !this.variant ||
        !this.variant.available ||
        this.mode === 'subscription' && !this.variant.planId
      ) {
        this.error(this.labels.box_unavailable);
        return;
      }

      this.busy = true;
      this.error('');
      this.form.setAttribute('aria-busy', 'true');

      const controls = this.querySelectorAll(
        '[data-cz-submit], [data-cz-back], [data-cz-close]'
      );

      controls.forEach(button => {
        button.disabled = true;
      });

      this.text('[data-cz-submit]', this.labels.adding);

      try {
        const response = await fetch(
          `${this.config.rootUrl}products/${encodeURIComponent(this.selection.handle)}.js`,
          { headers: { Accept: 'application/json' } }
        );

        if (!response.ok) {
          throw new Error(this.labels.selection_unavailable);
        }

        const selectedProduct = await response.json();

        const selectedVariant = selectedProduct.variants.find(
          variant =>
            String(variant.id) === String(this.selection.variantId)
        );

        if (!selectedVariant || !selectedVariant.available) {
          this.showStockError = true;
          throw new Error(this.labels.selection_unavailable);
        }

        const now = new Intl.DateTimeFormat('en-US', {
          timeZone: this.config.timeZone,
          year: 'numeric'
        }).format(new Date());

        const item = buildCartItem({
          variant: this.variant,
          recurring: this.mode === 'subscription',
          selection: this.selection,
          profile: this.profile(),
          season: this.season,
          year: now,
          hemisphere: this.hemisphere
        });

        const added = await fetch(this.config.cartAddUrl, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Accept: 'application/json'
          },
          body: JSON.stringify({ items: [item] })
        });

        let result;

        try {
          result = await added.json();
        } catch (_) {
          throw new Error(this.labels.add_uncertain);
        }

        if (!added.ok) {
          throw new Error(
            typeof result.description === 'string'
              ? result.description
              : this.labels.add_error
          );
        }

        window.location.assign(this.config.cartUrl);
      } catch (error) {
        if (this.showStockError) {
          this.selection = null;
          this.showStockError = false;

          this.querySelectorAll('[data-cz-choice]').forEach(input => {
            input.checked = false;
          });

          this.querySelectorAll('[data-cz-product]').forEach(card => {
            card.classList.remove('is-selected');
          });

          this.text(
            '[data-cz-selection-summary]',
            this.noneSelectedText
          );

          this.busy = false;
          this.showStep(0);
        }

        this.error(
          error instanceof TypeError
            ? this.labels.add_uncertain
            : error.message || this.labels.add_error
        );
      } finally {
        this.busy = false;
        this.form.removeAttribute('aria-busy');

        controls.forEach(button => {
          button.disabled = false;
        });

        this.text('[data-cz-submit]', this.labels.add_to_cart);
      }
    }
  }

  customElements.define('cz-seasonal-box', CZSeasonalBox);
})();